import React, {
    useRef,
    useState,
} from 'react'

import {
    PlusIcon,
    MinusIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline'


import {
    useGetOrgIntentionBoardsQuery,
} from '@/features/orgIntentionBoards/orgIntentionBoardsApiSlice'


import {
    useGetOrgIntentionCandidatesQuery,
} from '@/features/orgIntentionBoards/orgIntentionCandidatesApiSlice'


import {
    useGetOrgIntentionBoardCandidateCommunicationStatusQuery,
} from '@/features/orgIntentionBoards/orgIntentionCandidateCommunicationsApiSlice'


import {

    useGetOrgIntentionBoardCandidateOnboardingStatusQuery,

    useGetOrgIntentionBoardCandidateSlotStatusQuery,

    useUpdateOrgIntentionCandidateOnboardingMutation,

} from '@/features/orgIntentionBoards/orgIntentionCandidateOnboardingsApiSlice'


import useAuth
    from '@/hooks/useAuth'



const EtOrgInitiationBoard = ({
    userOrgId,
}) => {

    // First Created - 24 Sep 2026
    // Author - Atul Pandey
    // Purpose - Organization level Initiation Board
    //
    // Updated - 24 Sep 2026
    // Candidate enters Initiation when:
    //
    // finalDecision === "go"
    //
    // Candidate lifecycle finalization is NOT required.
    //
    // Updated - 24 Sep 2026
    // Added communication milestone status.
    //
    // Updated - 25 Sep 2026
    // Added Pre Program Actions:
    //
    // Contact Details
    // Digital Pic
    // AV Consent
    //
    // Updated - 25 Sep 2026
    // Added manual Pre Program Action management.
    //
    // Authorized:
    //
    // EtAdmin
    // EtCoach
    // OrgSponsor
    // OrgFacilitator
    //
    // Updated - 25 Sep 2026
    // Added SlotBoard based journey status for:
    //
    // Initial 1:1
    // Deep Dive
    //
    // Updated - 25 Sep 2026
    // Added explicit left / right journey scrolling controls.
    //
    // Updated - 25 Sep 2026
    // Added manual completion tracking for:
    //
    // Initial 1:1
    // Deep Dive
    //
    // Updated - 25 Sep 2026
    // Added event attendance milestones:
    //
    // Kickoff
    // Induction
    //
    // Event milestone progression:
    //
    // Pending
    // Confirmed
    // Attended
    // Not Required
    //
    // Journey sequence:
    //
    // Pre Program Actions
    // Initial 1:1
    // Kickoff
    // Induction
    // Deep Dive
    //
    // Future:
    //
    // User activation
    // Cohort transition
    //
    // Updated - 28 Sep 2026
    // Added compact status filters across initiation milestone columns
    //


    /*
    |--------------------------------------------------------------------------
    | 01 - State
    |--------------------------------------------------------------------------
    */

    const [
        selectedIntentionBoardId,
        setSelectedIntentionBoardId,
    ] =
        useState(
            null
        )
    
    /*
    |--------------------------------------------------------------------------
    | 02 - Get Organization Intention Boards
    |--------------------------------------------------------------------------
    */

    const {

        data:
            intentionBoardsData,

        isLoading:
            isIntentionBoardsLoading,

        isFetching:
            isIntentionBoardsFetching,

        isError:
            isIntentionBoardsError,

        error:
            intentionBoardsError,

    } =
        useGetOrgIntentionBoardsQuery(

            userOrgId
                ?
                {
                    orgId:
                        userOrgId,
                }
                :
                undefined,

            {
                skip:
                    !userOrgId,
            }

        )


    /*
    |--------------------------------------------------------------------------
    | 03 - Normalize Boards
    |--------------------------------------------------------------------------
    */

    const intentionBoards =
        intentionBoardsData
            ?.ids
            ?.map(
                id =>
                    intentionBoardsData
                        ?.entities[
                            id
                        ]
            )
            ?.filter(
                Boolean
            )
        ||
        []


    /*
    |--------------------------------------------------------------------------
    | 04 - Create Markup
    |--------------------------------------------------------------------------
    */

    const createMarkup =
        htmlContent => {

            return {
                __html:
                    htmlContent,
            }

        }


    /*
    |--------------------------------------------------------------------------
    | Return
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="
                bg-gray-200
                p-1
                mx-auto
                rounded-lg
                font-poppins
            "
        >


            {/*
            |--------------------------------------------------------------------------
            | Header
            |--------------------------------------------------------------------------
            */}

            <div
                className="
                    bg-white
                    m-1
                    p-4
                    rounded
                "
            >

                <p
                    className="
                        text-lg
                        font-bold
                        text-gray-800
                    "
                >
                    My Org's Initiation
                </p>

                <p
                    className="
                        text-xs
                        text-gray-500
                        italic
                        mt-1
                    "
                >
                    Track your organization's Intention Boards and the initiation journey of its candidates.
                </p>

            </div>



            {
                (
                    isIntentionBoardsLoading
                    ||
                    isIntentionBoardsFetching
                )
                &&
                (
                    <div
                        className="
                            bg-white
                            m-1
                            p-5
                            rounded
                        "
                    >

                        <p className="text-xs text-gray-500">
                            Loading Intention Boards...
                        </p>

                    </div>
                )
            }



            {
                isIntentionBoardsError
                &&
                (
                    <div
                        className="
                            bg-orange-50
                            m-1
                            p-4
                            rounded
                        "
                    >

                        <p
                            className="
                                text-sm
                                font-bold
                                text-orange-700
                            "
                        >
                            Unable to load Intention Boards
                        </p>

                        <p
                            className="
                                text-xs
                                text-orange-700
                                mt-1
                            "
                        >
                            {
                                intentionBoardsError
                                    ?.data
                                    ?.message
                                ||
                                'The Intention Boards could not be loaded.'
                            }
                        </p>

                    </div>
                )
            }



            {
                !isIntentionBoardsLoading
                &&
                !isIntentionBoardsFetching
                &&
                !isIntentionBoardsError
                &&
                intentionBoards.length ===
                    0
                &&
                (
                    <div
                        className="
                            bg-white
                            m-1
                            p-5
                            rounded
                        "
                    >

                        <p className="text-xs text-gray-500">
                            No Intention Boards are currently available for your organization.
                        </p>

                    </div>
                )
            }



            {
                intentionBoards.length >
                    0
                &&
                (
                    <div className="m-1">

                        {
                            intentionBoards.map(
                                (
                                    intentionBoard,
                                    boardIndex
                                ) => {

                                    const intentionBoardId =
                                        intentionBoard
                                            ?._id
                                        ||
                                        intentionBoard
                                            ?.id


                                    const isSelected =
                                        selectedIntentionBoardId ===
                                        intentionBoardId


                                    return (

                                        <IntentionBoardCard

                                            key={
                                                intentionBoardId
                                            }

                                            intentionBoard={
                                                intentionBoard
                                            }

                                            boardIndex={
                                                boardIndex
                                            }

                                            isSelected={
                                                isSelected
                                            }

                                            setSelectedIntentionBoardId={
                                                setSelectedIntentionBoardId
                                            }

                                            createMarkup={
                                                createMarkup
                                            }

                                        />

                                    )

                                }
                            )
                        }

                    </div>
                )
            }


        </div>

    )

}


/*
|--------------------------------------------------------------------------
| Initiation Status Filter Circle - 28 Sep 2026
|--------------------------------------------------------------------------
*/

const InitiationStatusFilter = ({ label, color, selected, onClick, disabled = false }) => (
    <button
        type="button"
        title={label}
        aria-label={`Filter ${label}`}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        className={`w-3 h-3 rounded-full shrink-0 border border-black/5 transition-all ${color} 
            ${selected 
                ? 'ring-2 ring-orange-600 ring-offset-2 scale-110 opacity-100' 
                : 'opacity-100 hover:scale-125'
            } 
            ${disabled ? 'cursor-default opacity-25' : 'cursor-pointer'}`
        }
    />
)

/*
|--------------------------------------------------------------------------
| Status filter configuration - same colors as the displayed status pills
|--------------------------------------------------------------------------
*/
const milestoneFilterOptions = {
    communications: [
        { status: 'all_sent', label: 'All Sent', color: 'bg-lime-500' },
        { status: 'partial', label: 'Partial', color: 'bg-orange-500' },
        { status: 'not_sent', label: 'Not Sent', color: 'bg-gray-400' },
    ],
    preProgram: [
        { status: 'complete', label: 'Complete', color: 'bg-lime-500' },
        { status: 'partial', label: 'Partial', color: 'bg-orange-500' },
        { status: 'not_started', label: 'Not Started', color: 'bg-gray-400' },
    ],
    initial1on1: [
        { status: 'booked', label: 'Booked', color: 'bg-blue-500' },
        { status: 'completed', label: 'Completed', color: 'bg-lime-500' },
        { status: 'not_booked', label: 'Not Booked', color: 'bg-gray-400' },
        { status: 'not_required', label: 'Not Required', color: 'bg-blue-300' },
    ],
    kickoff: [
        { status: 'pending', label: 'Pending', color: 'bg-gray-400' },
        { status: 'confirmed', label: 'Confirmed', color: 'bg-blue-500' },
        { status: 'attended', label: 'Attended', color: 'bg-lime-500' },
        { status: 'not_required', label: 'Not Required', color: 'bg-purple-400' },
    ],
    induction: [
        { status: 'pending', label: 'Pending', color: 'bg-gray-400' },
        { status: 'confirmed', label: 'Confirmed', color: 'bg-blue-500' },
        { status: 'attended', label: 'Attended', color: 'bg-lime-500' },
        { status: 'not_required', label: 'Not Required', color: 'bg-purple-400' },
    ],
    deepDive: [
        { status: 'booked', label: 'Booked', color: 'bg-blue-500' },
        { status: 'completed', label: 'Completed', color: 'bg-lime-500' },
        { status: 'not_booked', label: 'Not Booked', color: 'bg-gray-400' },
        { status: 'not_required', label: 'Not Required', color: 'bg-blue-300' },
    ],
}

const getMeetingFilterStatus = (completion, booking) => {
    const completionStatus = normalizeValue(completion?.status || 'pending')
    const bookingStatus = normalizeValue(booking?.status)
    if (completionStatus === 'completed') return 'completed'
    if (completionStatus === 'not_required') return 'not_required'
    if (bookingStatus === 'booked') return 'booked'
    return 'not_booked'
}

const MilestoneHeaderFilters = ({ milestone, selectedStatuses, onToggle, disabled = false }) => (
    <div className="flex items-center gap-2 mt-2">
        {(milestoneFilterOptions[milestone] || []).map(option => (
            <InitiationStatusFilter
                key={option.status}
                label={option.label}
                color={option.color}
                selected={selectedStatuses.includes(option.status)}
                onClick={() => onToggle(milestone, option.status)}
                disabled={disabled}
            />
        ))}
    </div>
)


/*
|--------------------------------------------------------------------------
| Intention Board Card
|--------------------------------------------------------------------------
*/

const IntentionBoardCard = ({

    intentionBoard,

    boardIndex,

    isSelected,

    setSelectedIntentionBoardId,

    createMarkup,

}) => {


    const intentionBoardId =
        intentionBoard
            ?._id
        ||
        intentionBoard
            ?.id


    const [
        showFullBoardNarrative,
        setShowFullBoardNarrative,
    ] =
        useState(
            false
        )


    return (

        <div
            className="
                bg-white
                rounded
                mb-1
                overflow-hidden
            "
        >


            <div
                className="
                    grid
                    grid-cols-12
                    items-start
                    p-4
                "
            >


                <div
                    className="
                        col-span-12
                        lg:col-span-9
                        flex
                        justify-start
                        items-start
                    "
                >


                    <button
                        type="button"

                        onClick={
                            () =>
                                setSelectedIntentionBoardId(
                                    previous =>
                                        previous ===
                                        intentionBoardId
                                            ?
                                            null
                                            :
                                            intentionBoardId
                                )
                        }

                        className="
                            mt-1
                            mr-2
                            h-7
                            w-7
                            rounded-full
                            flex
                            justify-center
                            items-center
                            cursor-pointer
                            hover:bg-orange-50
                            shrink-0
                        "
                    >

                        {
                            isSelected
                                ?
                                (
                                    <MinusIcon
                                        className="
                                            h-5
                                            w-5
                                            text-orange-600
                                        "
                                    />
                                )
                                :
                                (
                                    <PlusIcon
                                        className="
                                            h-5
                                            w-5
                                            text-orange-600
                                        "
                                    />
                                )
                        }

                    </button>



                    <div
                        className="
                            h-8
                            w-8
                            rounded-full
                            bg-orange-100
                            flex
                            justify-center
                            items-center
                            mr-3
                            mt-0.5
                            shrink-0
                        "
                    >

                        <p
                            className="
                                text-xs
                                font-bold
                                text-orange-700
                            "
                        >
                            {
                                boardIndex +
                                1
                            }
                        </p>

                    </div>



                    <div
                        className="
                            min-w-0
                            flex-1
                        "
                    >


                        <div
                            className="
                                flex
                                justify-start
                                items-center
                                flex-wrap
                                gap-1
                            "
                        >

                            <p
                                className="
                                    text-lg
                                    font-bold
                                    text-gray-800
                                "
                            >
                                {
                                    intentionBoard
                                        ?.boardName
                                    ||
                                    'Intention Board'
                                }
                            </p>


                            <BoardStatusChip
                                value={
                                    intentionBoard
                                        ?.boardStatus
                                }
                            />


                            <ActiveChip
                                isActive={
                                    intentionBoard
                                        ?.isActive
                                }
                            />

                        </div>



                        {
                            intentionBoard
                                ?.headline
                            &&
                            (
                                <p
                                    className="
                                        text-xs
                                        text-gray-600
                                        mt-1
                                    "
                                >
                                    {
                                        intentionBoard
                                            ?.headline
                                    }
                                </p>
                            )
                        }



                        {
                            intentionBoard
                                ?.boardNarrative
                            &&
                            (
                                <div
                                    className="
                                        mt-3
                                        bg-gray-50
                                        rounded
                                        p-3
                                        max-w-5xl
                                    "
                                >

                                    <div
                                        className={`
                                            text-xs
                                            text-gray-700
                                            leading-5

                                            [&_p]:mb-2

                                            [&_ul]:list-disc
                                            [&_ul]:ml-5
                                            [&_ul]:mb-2

                                            [&_ol]:list-decimal
                                            [&_ol]:ml-5
                                            [&_ol]:mb-2

                                            [&_li]:mb-1

                                            [&_strong]:font-bold
                                            [&_strong]:text-gray-900

                                            [&_em]:italic

                                            [&_a]:text-orange-600
                                            [&_a]:underline

                                            ${
                                                showFullBoardNarrative
                                                    ?
                                                    ''
                                                    :
                                                    'line-clamp-3'
                                            }
                                        `}

                                        dangerouslySetInnerHTML={
                                            createMarkup(
                                                intentionBoard
                                                    ?.boardNarrative
                                            )
                                        }
                                    />


                                    <div
                                        className="
                                            flex
                                            justify-end
                                            items-center
                                            mt-2
                                        "
                                    >

                                        <button
                                            type="button"

                                            onClick={
                                                event => {

                                                    event
                                                        .stopPropagation()


                                                    setShowFullBoardNarrative(
                                                        previous =>
                                                            !previous
                                                    )

                                                }
                                            }

                                            className="
                                                text-xs
                                                font-bold
                                                text-orange-600
                                                hover:text-orange-700
                                                hover:underline
                                                cursor-pointer
                                            "
                                        >
                                            {
                                                showFullBoardNarrative
                                                    ?
                                                    'Read Less'
                                                    :
                                                    'Read More'
                                            }
                                        </button>

                                    </div>

                                </div>
                            )
                        }


                    </div>


                </div>



                <div
                    className="
                        col-span-12
                        lg:col-span-3
                        flex
                        justify-start
                        lg:justify-end
                        items-start
                        mt-3
                        lg:mt-1
                    "
                >

                    {
                        intentionBoard
                            ?.responseDeadline
                        &&
                        (
                            <div className="lg:text-right">

                                <p
                                    className="
                                        text-[9px]
                                        uppercase
                                        font-bold
                                        text-gray-400
                                    "
                                >
                                    Response Deadline
                                </p>

                                <p
                                    className="
                                        text-xs
                                        font-bold
                                        text-gray-700
                                        mt-1
                                    "
                                >
                                    {
                                        formatDate(
                                            intentionBoard
                                                ?.responseDeadline
                                        )
                                    }
                                </p>

                            </div>
                        )
                    }

                </div>


            </div>



            {
                isSelected
                &&
                (
                    <div
                        className="
                            border-t
                            border-gray-200
                            bg-gray-100
                            p-1
                        "
                    >

                        <EtOrgInitiationCandidateBoard
                            intentionBoard={
                                intentionBoard
                            }
                        />

                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Candidate Initiation Board
|--------------------------------------------------------------------------
*/

const EtOrgInitiationCandidateBoard = ({
    intentionBoard,
}) => {


    /*
    |--------------------------------------------------------------------------
    | Authorization
    |--------------------------------------------------------------------------
    */

    const {

        isEtAdmin,

        isEtCoach,

        isSponsor,

        isOrgFacilitator,

    } =
        useAuth()


    const canManageInitiation =
        (
            isEtAdmin
            ||
            isEtCoach
            ||
            isSponsor
            ||
            isOrgFacilitator
        )


    /*
    |--------------------------------------------------------------------------
    | Journey Scroll
    |--------------------------------------------------------------------------
    */

    const journeyScrollRef =
        useRef(
            null
        )


    const [
        canScrollLeft,
        setCanScrollLeft,
    ] =
        useState(
            false
        )


    const [
        canScrollRight,
        setCanScrollRight,
    ] =
        useState(
            true
        )


    /*
    |--------------------------------------------------------------------------
    | Board / Organization
    |--------------------------------------------------------------------------
    */

    const intentionBoardId =
        intentionBoard
            ?._id
        ||
        intentionBoard
            ?.id


    const orgId =
        intentionBoard
            ?.orgId
            ?._id
        ||
        intentionBoard
            ?.orgId
        ||
        null


    /*
    |--------------------------------------------------------------------------
    | Milestone Filters - 28 Sep 2026
    | One independent selection set per expanded Intention Board.
    |--------------------------------------------------------------------------
    */
    const [milestoneFilters, setMilestoneFilters] = useState({})

    const toggleMilestoneFilter = (milestone, status) => {
        setMilestoneFilters(previous => {
            const current = previous[milestone] || []
            const next = current.includes(status)
                ? current.filter(item => item !== status)
                : [...current, status]
            return { ...previous, [milestone]: next }
        })
    }

    /*
    |--------------------------------------------------------------------------
    | Scroll State
    |--------------------------------------------------------------------------
    */

    const updateJourneyScrollState =
        () => {

            const scrollContainer =
                journeyScrollRef
                    ?.current


            if (
                !scrollContainer
            ) {

                return

            }


            const {

                scrollLeft,

                scrollWidth,

                clientWidth,

            } =
                scrollContainer


            setCanScrollLeft(
                scrollLeft >
                5
            )


            setCanScrollRight(
                scrollLeft +
                clientWidth <
                scrollWidth -
                5
            )

        }


    /*
    |--------------------------------------------------------------------------
    | Scroll Journey
    |--------------------------------------------------------------------------
    */

    const scrollJourney =
        direction => {

            const scrollContainer =
                journeyScrollRef
                    ?.current


            if (
                !scrollContainer
            ) {

                return

            }


            const scrollAmount =
                Math.max(
                    420,
                    scrollContainer
                        .clientWidth *
                    0.55
                )


            scrollContainer
                .scrollBy({

                    left:
                        direction ===
                        'left'
                            ?
                            -scrollAmount
                            :
                            scrollAmount,

                    behavior:
                        'smooth',

                })


            window.setTimeout(
                updateJourneyScrollState,
                350
            )

        }


    /*
    |--------------------------------------------------------------------------
    | Get Candidates
    |--------------------------------------------------------------------------
    */

    const {

        data:
            candidatesData,

        isLoading:
            isCandidatesLoading,

        isFetching:
            isCandidatesFetching,

        isError:
            isCandidatesError,

        error:
            candidatesError,

    } =
        useGetOrgIntentionCandidatesQuery(

            {
                intentionBoardId,
            },

            {
                skip:
                    !intentionBoardId,
            }

        )


    /*
    |--------------------------------------------------------------------------
    | Communications
    |--------------------------------------------------------------------------
    */

    const {

        data:
            communicationStatusData,

        isLoading:
            isCommunicationStatusLoading,

        isFetching:
            isCommunicationStatusFetching,

        isError:
            isCommunicationStatusError,

        error:
            communicationStatusError,

    } =
        useGetOrgIntentionBoardCandidateCommunicationStatusQuery(

            {
                orgId,

                intentionBoardId,
            },

            {
                skip:
                    !orgId
                    ||
                    !intentionBoardId,
            }

        )


    /*
    |--------------------------------------------------------------------------
    | Onboarding / Milestone Status
    |--------------------------------------------------------------------------
    */

    const {

        data:
            onboardingStatusData,

        isLoading:
            isOnboardingStatusLoading,

        isFetching:
            isOnboardingStatusFetching,

        isError:
            isOnboardingStatusError,

        error:
            onboardingStatusError,

    } =
        useGetOrgIntentionBoardCandidateOnboardingStatusQuery(

            {
                orgId,

                intentionBoardId,
            },

            {
                skip:
                    !orgId
                    ||
                    !intentionBoardId,
            }

        )


    /*
    |--------------------------------------------------------------------------
    | SlotBoard Status
    |--------------------------------------------------------------------------
    */

    const {

        data:
            slotStatusData,

        isLoading:
            isSlotStatusLoading,

        isFetching:
            isSlotStatusFetching,

        isError:
            isSlotStatusError,

        error:
            slotStatusError,

    } =
        useGetOrgIntentionBoardCandidateSlotStatusQuery(

            {
                orgId,

                intentionBoardId,
            },

            {
                skip:
                    !orgId
                    ||
                    !intentionBoardId,
            }

        )


    /*
    |--------------------------------------------------------------------------
    | Normalize Candidates
    |--------------------------------------------------------------------------
    */

    const candidates =
        candidatesData
            ?.ids
            ?.map(
                id =>
                    candidatesData
                        ?.entities[
                            id
                        ]
            )
            ?.filter(
                Boolean
            )
        ||
        []


    const sortedCandidates =
        (
            candidates
            ||
            []
        )
            .slice()
            .sort(
                (
                    a,
                    b
                ) => {

                    const aName =
                        a
                            ?.candidateName
                        ||
                        ''

                    const bName =
                        b
                            ?.candidateName
                        ||
                        ''


                    return aName
                        .localeCompare(
                            bName
                        )

                }
            )


    /*
    |--------------------------------------------------------------------------
    | Initiation Eligible
    |--------------------------------------------------------------------------
    */

    const initiationCandidates =
        (
            sortedCandidates
            ||
            []
        )
            .filter(
                candidate =>
                    normalizeValue(
                        candidate
                            ?.finalDecision
                    ) ===
                    'go'
            )


    /*
    |--------------------------------------------------------------------------
    | Communication Map
    |--------------------------------------------------------------------------
    */

    const communicationStatusMap =
        new Map(
            (
                communicationStatusData
                    ?.candidates
                ||
                []
            )
                .map(
                    candidateStatus => [

                        String(
                            candidateStatus
                                ?.intentionCandidateId
                            ||
                            ''
                        ),

                        candidateStatus
                            ?.communicationStatus
                        ||
                        {},

                    ]
                )
                .filter(
                    (
                        [
                            candidateId,
                        ]
                    ) =>
                        Boolean(
                            candidateId
                        )
                )
        )


    /*
    |--------------------------------------------------------------------------
    | Onboarding Map
    |--------------------------------------------------------------------------
    */

    const onboardingStatusMap =
        new Map(
            (
                onboardingStatusData
                    ?.candidates
                ||
                []
            )
                .map(
                    candidateStatus => [

                        String(
                            candidateStatus
                                ?.intentionCandidateId
                            ||
                            ''
                        ),

                        candidateStatus,

                    ]
                )
                .filter(
                    (
                        [
                            candidateId,
                        ]
                    ) =>
                        Boolean(
                            candidateId
                        )
                )
        )


    /*
    |--------------------------------------------------------------------------
    | Slot Map
    |--------------------------------------------------------------------------
    */

    const slotStatusMap =
        new Map(
            (
                slotStatusData
                    ?.candidates
                ||
                []
            )
                .map(
                    candidateStatus => [

                        String(
                            candidateStatus
                                ?.intentionCandidateId
                            ||
                            ''
                        ),

                        candidateStatus,

                    ]
                )
                .filter(
                    (
                        [
                            candidateId,
                        ]
                    ) =>
                        Boolean(
                            candidateId
                        )
                )
        )


    /*
    |--------------------------------------------------------------------------
    | Counts
    |--------------------------------------------------------------------------
    */

    const totalBoardCandidates =
        sortedCandidates
            .length


    const confirmedGoCount =
        initiationCandidates
            .length


    const intentionFinalizedCount =
        initiationCandidates
            .filter(
                candidate =>
                    normalizeValue(
                        candidate
                            ?.decisionStatus
                    ) ===
                    'finalized'
            )
            .length


    const initiationOpenCount =
        initiationCandidates
            .filter(
                candidate =>
                    normalizeValue(
                        candidate
                            ?.decisionStatus
                    ) !==
                    'finalized'
            )
            .length


    const isCommunicationStatusPending =
        (
            isCommunicationStatusLoading
            ||
            isCommunicationStatusFetching
        )


    const isOnboardingStatusPending =
        (
            isOnboardingStatusLoading
            ||
            isOnboardingStatusFetching
        )


    const isSlotStatusPending =
        (
            isSlotStatusLoading
            ||
            isSlotStatusFetching
        )


    /*
    |--------------------------------------------------------------------------
    | Apply milestone filters to the same status used by the visible pills.
    | OR within each column; AND across columns. None or all selected = All.
    |--------------------------------------------------------------------------
    */
    const hasActiveMilestoneFilters = Object.entries(milestoneFilterOptions)
        .some(([milestone, options]) => {
            const selected = milestoneFilters[milestone] || []
            return selected.length > 0 && selected.length < options.length
        })

    const isFilterDataPending = isCommunicationStatusPending || isOnboardingStatusPending || isSlotStatusPending
    const isFilterDataUnavailable = isCommunicationStatusError || isOnboardingStatusError || isSlotStatusError

    const filteredInitiationCandidates = initiationCandidates.filter(candidate => {
        if (!hasActiveMilestoneFilters || isFilterDataPending || isFilterDataUnavailable) return true

        const candidateId = String(candidate?.intentionCandidateId || candidate?._id || candidate?.id || '')
        const communicationStatus = communicationStatusMap.get(candidateId) || {}
        const onboardingStatus = onboardingStatusMap.get(candidateId) || {}
        const slotStatus = slotStatusMap.get(candidateId) || {}

        const statuses = {
            communications: getCommunicationSummary({ communicationStatus, isLoading: false, isError: false }),
            preProgram: getPreProgramActionSummary({ onboardingStatus, isLoading: false, isError: false }),
            initial1on1: getMeetingFilterStatus(onboardingStatus?.milestones?.initial1on1, slotStatus?.initial1on1),
            kickoff: normalizeValue(onboardingStatus?.milestones?.kickoff?.status || 'pending'),
            induction: normalizeValue(onboardingStatus?.milestones?.induction?.status || 'pending'),
            deepDive: getMeetingFilterStatus(onboardingStatus?.milestones?.deepDive, slotStatus?.deepDive),
        }

        return Object.entries(milestoneFilterOptions).every(([milestone, options]) => {
            const selected = milestoneFilters[milestone] || []
            if (selected.length === 0 || selected.length === options.length) return true
            return selected.includes(statuses[milestone])
        })
    })

    /*
    |--------------------------------------------------------------------------
    | Candidate Loading
    |--------------------------------------------------------------------------
    */

    if (
        isCandidatesLoading
        ||
        isCandidatesFetching
    ) {

        return (

            <div
                className="
                    bg-white
                    rounded
                    m-1
                    p-4
                "
            >

                <p className="text-xs text-gray-500">
                    Loading candidates...
                </p>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | Candidate Error
    |--------------------------------------------------------------------------
    */

    if (
        isCandidatesError
    ) {

        return (

            <div
                className="
                    bg-orange-50
                    rounded
                    m-1
                    p-4
                "
            >

                <p
                    className="
                        text-sm
                        text-orange-700
                        font-bold
                    "
                >
                    Unable to load candidates
                </p>

                <p
                    className="
                        text-xs
                        text-orange-700
                        mt-1
                    "
                >
                    {
                        candidatesError
                            ?.data
                            ?.message
                        ||
                        'Candidate information could not be loaded.'
                    }
                </p>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | Return
    |--------------------------------------------------------------------------
    */

    return (

        <div>


            <div
                className="
                    bg-white
                    rounded
                    m-1
                    p-4
                "
            >

                <div
                    className="
                        flex
                        justify-between
                        items-start
                        gap-3
                    "
                >

                    <div>

                        <p
                            className="
                                text-lg
                                font-bold
                                text-gray-800
                            "
                        >
                            Candidate Initiation Journey
                        </p>

                        <p
                            className="
                                text-xs
                                text-gray-500
                                mt-1
                            "
                        >
                            Candidates enter Initiation once their formal Intention Board decision has been confirmed as Go.
                        </p>

                    </div>


                    {
                        (
                            isCommunicationStatusPending
                            ||
                            isOnboardingStatusPending
                            ||
                            isSlotStatusPending
                        )
                        &&
                        (
                            <p
                                className="
                                    text-[9px]
                                    font-bold
                                    text-orange-600
                                    shrink-0
                                "
                            >
                                Updating journey...
                            </p>
                        )
                    }

                </div>

            </div>



            {
                isCommunicationStatusError
                &&
                (
                    <JourneyDataError
                        title="Communication status is currently unavailable."
                        error={
                            communicationStatusError
                        }
                    />
                )
            }



            {
                isOnboardingStatusError
                &&
                (
                    <JourneyDataError
                        title="Initiation milestone status is currently unavailable."
                        error={
                            onboardingStatusError
                        }
                    />
                )
            }



            {
                isSlotStatusError
                &&
                (
                    <JourneyDataError
                        title="Initial 1:1 / Deep Dive booking status is currently unavailable."
                        error={
                            slotStatusError
                        }
                    />
                )
            }



            <div
                className="
                    grid
                    grid-cols-2
                    md:grid-cols-4
                    gap-1
                    m-1
                "
            >

                <SummaryCard
                    label="Board Candidates"
                    value={
                        totalBoardCandidates
                    }
                    className="bg-white"
                    labelClassName="text-gray-500"
                />


                <SummaryCard
                    label="Confirmed Go"
                    value={
                        confirmedGoCount
                    }
                    className="bg-lime-50"
                    labelClassName="text-lime-700"
                />


                <SummaryCard
                    label="Intention Finalized"
                    value={
                        intentionFinalizedCount
                    }
                    className="bg-purple-50"
                    labelClassName="text-purple-700"
                />


                <SummaryCard
                    label="Initiation Open"
                    value={
                        initiationOpenCount
                    }
                    className="bg-orange-50"
                    labelClassName="text-orange-700"
                />

            </div>



            {
                initiationCandidates.length ===
                    0
                &&
                (
                    <div
                        className="
                            bg-white
                            rounded
                            m-1
                            p-5
                        "
                    >

                        <p
                            className="
                                text-sm
                                font-bold
                                text-gray-700
                            "
                        >
                            No candidates have entered Initiation yet.
                        </p>

                    </div>
                )
            }



            {
                initiationCandidates.length >
                    0
                &&
                (
                    <div
                        className="
                            bg-white
                            rounded
                            m-1
                            overflow-hidden
                        "
                    >


                        {/*
                        |--------------------------------------------------------------------------
                        | Journey Navigation
                        |--------------------------------------------------------------------------
                        */}

                        <div
                            className="
                                flex
                                justify-between
                                items-center
                                px-4
                                py-2
                                border-b
                                border-gray-100
                            "
                        >

                            <p
                                className="
                                    text-[9px]
                                    font-bold
                                    uppercase
                                    text-gray-400
                                "
                            >
                                Navigate Journey
                            </p>


                            <div
                                className="
                                    flex
                                    justify-end
                                    items-center
                                    gap-2
                                "
                            >

                                <JourneyScrollButton
                                    direction="left"
                                    enabled={
                                        canScrollLeft
                                    }
                                    onClick={
                                        () =>
                                            scrollJourney(
                                                'left'
                                            )
                                    }
                                />


                                <JourneyScrollButton
                                    direction="right"
                                    enabled={
                                        canScrollRight
                                    }
                                    onClick={
                                        () =>
                                            scrollJourney(
                                                'right'
                                            )
                                    }
                                />

                            </div>

                        </div>



                        {/*
                        |--------------------------------------------------------------------------
                        | Scroll Surface
                        |--------------------------------------------------------------------------
                        */}

                        <div
                            ref={
                                journeyScrollRef
                            }

                            onScroll={
                                updateJourneyScrollState
                            }

                            className="
                                max-h-[560px]
                                overflow-x-auto
                                overflow-y-auto
                                [&::-webkit-scrollbar]:hidden
                            "

                            style={{

                                scrollbarWidth:
                                    'none',

                                msOverflowStyle:
                                    'none',

                            }}
                        >

                            <div
                                className="
                                    min-w-[2450px]
                                "
                            >


                                {/*
                                |--------------------------------------------------------------------------
                                | Header
                                |--------------------------------------------------------------------------
                                */}

                                <div
                                    className="
                                        grid
                                        grid-cols-[300px_150px_150px_160px_170px_260px_320px_250px_220px_220px_250px]
                                        items-start
                                        bg-gray-50
                                        border-b
                                        border-gray-200
                                        sticky
                                        top-0
                                        z-30
                                    "
                                >


                                    <div
                                        className="
                                            sticky
                                            left-0
                                            z-40
                                            bg-gray-50
                                            border-r
                                            border-gray-200
                                            px-4
                                            py-3
                                            h-full
                                            self-stretch
                                        "
                                    >
                                        <TableHeading>
                                            Candidate
                                        </TableHeading>
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Intention Status
                                        </TableHeading>
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Decision Status
                                        </TableHeading>
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Candidate Decision
                                        </TableHeading>
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Initiation
                                        </TableHeading>
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Communications
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="communications"
                                            selectedStatuses={milestoneFilters.communications || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Pre Program Actions
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="preProgram"
                                            selectedStatuses={milestoneFilters.preProgram || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Initial 1:1
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="initial1on1"
                                            selectedStatuses={milestoneFilters.initial1on1 || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Kickoff
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="kickoff"
                                            selectedStatuses={milestoneFilters.kickoff || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Induction
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="induction"
                                            selectedStatuses={milestoneFilters.induction || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                    <div className="px-4 py-3">
                                        <TableHeading>
                                            Deep Dive
                                        </TableHeading>
                                        <MilestoneHeaderFilters
                                            milestone="deepDive"
                                            selectedStatuses={milestoneFilters.deepDive || []}
                                            onToggle={toggleMilestoneFilter}
                                            disabled={isFilterDataPending || isFilterDataUnavailable}
                                        />
                                    </div>


                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | Candidate Rows
                                |--------------------------------------------------------------------------
                                */}

                                {
                                    filteredInitiationCandidates.map(
                                            (
                                                candidate,
                                                candidateIndex
                                            ) => {


                                                const candidateId =
                                                    candidate
                                                        ?.intentionCandidateId
                                                    ||
                                                    candidate
                                                        ?._id
                                                    ||
                                                    candidate
                                                        ?.id


                                                const candidateCommunicationStatus =
                                                    communicationStatusMap
                                                        .get(
                                                            String(
                                                                candidateId
                                                                ||
                                                                ''
                                                            )
                                                        )
                                                    ||
                                                    null


                                                const candidateOnboardingStatus =
                                                    onboardingStatusMap
                                                        .get(
                                                            String(
                                                                candidateId
                                                                ||
                                                                ''
                                                            )
                                                        )
                                                    ||
                                                    null


                                                const candidateSlotStatus =
                                                    slotStatusMap
                                                        .get(
                                                            String(
                                                                candidateId
                                                                ||
                                                                ''
                                                            )
                                                        )
                                                    ||
                                                    null


                                                return (

                                                    <CandidateRow

                                                        key={
                                                            candidateId
                                                            ||
                                                            candidateIndex
                                                        }

                                                        orgId={
                                                            orgId
                                                        }

                                                        intentionBoardId={
                                                            intentionBoardId
                                                        }

                                                        candidate={
                                                            candidate
                                                        }

                                                        candidateIndex={
                                                            candidateIndex
                                                        }

                                                        communicationStatus={
                                                            candidateCommunicationStatus
                                                        }

                                                        onboardingStatus={
                                                            candidateOnboardingStatus
                                                        }

                                                        slotStatus={
                                                            candidateSlotStatus
                                                        }

                                                        canManageInitiation={
                                                            canManageInitiation
                                                        }

                                                        isCommunicationStatusLoading={
                                                            isCommunicationStatusPending
                                                        }

                                                        isCommunicationStatusError={
                                                            isCommunicationStatusError
                                                        }

                                                        isOnboardingStatusLoading={
                                                            isOnboardingStatusPending
                                                        }

                                                        isOnboardingStatusError={
                                                            isOnboardingStatusError
                                                        }

                                                        isSlotStatusLoading={
                                                            isSlotStatusPending
                                                        }

                                                        isSlotStatusError={
                                                            isSlotStatusError
                                                        }

                                                    />

                                                )

                                            }
                                        )
                                }

                                {filteredInitiationCandidates.length === 0 && initiationCandidates.length > 0 && (
                                    <div className="sticky left-0 w-[300px] p-4 text-xs text-gray-500">
                                        No candidates match the selected filters.
                                    </div>
                                )}

                            </div>

                        </div>


                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Candidate Row
|--------------------------------------------------------------------------
*/

const CandidateRow = ({

    orgId,

    intentionBoardId,

    candidate,

    candidateIndex,

    communicationStatus,

    onboardingStatus,

    slotStatus,

    canManageInitiation,

    isCommunicationStatusLoading = false,

    isCommunicationStatusError = false,

    isOnboardingStatusLoading = false,

    isOnboardingStatusError = false,

    isSlotStatusLoading = false,

    isSlotStatusError = false,

}) => {


    const [
        showCommunicationDetails,
        setShowCommunicationDetails,
    ] =
        useState(
            false
        )


    const [
        showOnboardingDetails,
        setShowOnboardingDetails,
    ] =
        useState(
            false
        )


    const [
        updatingActionName,
        setUpdatingActionName,
    ] =
        useState(
            null
        )


    const [
        onboardingUpdateError,
        setOnboardingUpdateError,
    ] =
        useState(
            ''
        )


    const [

        updateCandidateOnboarding,

        {
            isLoading:
                isUpdatingOnboarding,
        },

    ] =
        useUpdateOrgIntentionCandidateOnboardingMutation()


    const intentionCandidateId =
        candidate
            ?.intentionCandidateId
        ||
        candidate
            ?._id
        ||
        candidate
            ?.id


    const candidateStatus =
        candidate
            ?.candidateStatus
        ||
        'nominated'


    const decisionStatus =
        candidate
            ?.decisionStatus
        ||
        'pending'


    const finalDecision =
        candidate
            ?.finalDecision
        ||
        'pending'


    const initiationStatus =
        getInitiationStatus({

            decisionStatus,

            finalDecision,

        })


    const communicationSummary =
        getCommunicationSummary({

            communicationStatus,

            isLoading:
                isCommunicationStatusLoading,

            isError:
                isCommunicationStatusError,

        })


    const onboardingSummary =
        getPreProgramActionSummary({

            onboardingStatus,

            isLoading:
                isOnboardingStatusLoading,

            isError:
                isOnboardingStatusError,

        })


    /*
    |--------------------------------------------------------------------------
    | Update Journey Action
    |--------------------------------------------------------------------------
    |
    | Handles:
    |
    | contactDetails
    | digitalPic
    | avConsent
    | initial1on1
    | kickoff
    | induction
    | deepDive
    |
    */

    const handleUpdateJourneyAction =
        async ({
            actionName,
            status,
            currentStatus,
        }) => {


            if (
                !canManageInitiation
            ) {

                return

            }


            if (
                !orgId
                ||
                !intentionBoardId
                ||
                !intentionCandidateId
                ||
                !actionName
                ||
                !status
            ) {

                return

            }


            if (
                normalizeValue(
                    currentStatus
                ) ===
                normalizeValue(
                    status
                )
            ) {

                return

            }


            setOnboardingUpdateError(
                ''
            )


            setUpdatingActionName(
                actionName
            )


            try {

                await updateCandidateOnboarding({

                    orgId,

                    intentionBoardId,

                    intentionCandidateId,

                    actionName,

                    status,

                })
                    .unwrap()

            } catch (
                error
            ) {

                console.error(
                    'Unable to update candidate Initiation journey:',
                    error
                )


                setOnboardingUpdateError(
                    error
                        ?.data
                        ?.message
                    ||
                    error
                        ?.error
                    ||
                    'Unable to update Initiation journey.'
                )

            } finally {

                setUpdatingActionName(
                    null
                )

            }

        }


    return (

        <div
            className="
                grid
                grid-cols-[300px_150px_150px_160px_170px_260px_320px_250px_220px_220px_250px]
                items-stretch
                border-b
                border-gray-200
                last:border-b-0
                group
                hover:bg-orange-50/30
                transition
            "
        >


            {/*
            |--------------------------------------------------------------------------
            | Candidate
            |--------------------------------------------------------------------------
            */}

            <div
                className="
                    sticky
                    left-0
                    z-20
                    bg-white
                    group-hover:bg-orange-50
                    border-r
                    border-gray-200
                    px-4
                    py-4
                    h-full
                "
            >

                <div
                    className="
                        flex
                        justify-start
                        items-start
                    "
                >

                    <div
                        className="
                            h-8
                            w-8
                            rounded-full
                            bg-orange-100
                            flex
                            justify-center
                            items-center
                            mr-3
                            shrink-0
                        "
                    >

                        <p
                            className="
                                text-xs
                                font-bold
                                text-orange-700
                            "
                        >
                            {
                                candidateIndex +
                                1
                            }
                        </p>

                    </div>


                    <div
                        className="
                            min-w-0
                            flex-1
                        "
                    >

                        <p
                            className="
                                text-xs
                                font-bold
                                text-gray-800
                                leading-snug
                                break-words
                            "
                        >
                            {
                                candidate
                                    ?.candidateName
                                ||
                                'Unnamed Candidate'
                            }
                        </p>


                        {
                            candidate
                                ?.candidateEmail
                            &&
                            (
                                <p
                                    className="
                                        text-[9px]
                                        text-gray-500
                                        mt-1
                                        break-all
                                    "
                                >
                                    {
                                        candidate
                                            ?.candidateEmail
                                    }
                                </p>
                            )
                        }

                    </div>

                </div>

            </div>



            <div className="px-4 py-4">

                <CandidateStatusChip
                    value={
                        candidateStatus
                    }
                />

            </div>



            <div className="px-4 py-4">

                <DecisionChip
                    value={
                        decisionStatus
                    }
                />

            </div>



            <div className="px-4 py-4">

                <FinalDecisionChip
                    value={
                        finalDecision
                    }
                />

            </div>



            <div className="px-4 py-4">

                <InitiationChip
                    value={
                        initiationStatus
                    }
                />

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Communications
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <button
                    type="button"

                    disabled={
                        isCommunicationStatusLoading
                        ||
                        isCommunicationStatusError
                    }

                    onClick={
                        () =>
                            setShowCommunicationDetails(
                                previous =>
                                    !previous
                            )
                    }

                    className="
                        flex
                        justify-start
                        items-center
                        gap-1
                        disabled:cursor-default
                    "
                >

                    <CommunicationSummaryChip
                        value={
                            communicationSummary
                        }
                    />


                    {
                        !isCommunicationStatusLoading
                        &&
                        !isCommunicationStatusError
                        &&
                        (
                            showCommunicationDetails
                                ?
                                (
                                    <ChevronUpIcon
                                        className="
                                            h-3
                                            w-3
                                            text-orange-600
                                        "
                                    />
                                )
                                :
                                (
                                    <ChevronDownIcon
                                        className="
                                            h-3
                                            w-3
                                            text-orange-600
                                        "
                                    />
                                )
                        )
                    }

                </button>


                {
                    showCommunicationDetails
                    &&
                    !isCommunicationStatusLoading
                    &&
                    !isCommunicationStatusError
                    &&
                    (
                        <div
                            className="
                                mt-2
                                rounded
                                bg-gray-50
                                p-2
                                space-y-2
                            "
                        >

                            <CommunicationMilestone
                                label="Welcome"
                                milestone={
                                    communicationStatus
                                        ?.welcome
                                }
                            />


                            <CommunicationMilestone
                                label="Pre Program Actions"
                                milestone={
                                    communicationStatus
                                        ?.onboarding_update
                                }
                            />


                            <CommunicationMilestone
                                label="Initial 1:1"
                                milestone={
                                    communicationStatus
                                        ?.initial_1on1
                                }
                            />


                            <CommunicationMilestone
                                label="Deep Dive"
                                milestone={
                                    communicationStatus
                                        ?.deep_dive
                                }
                            />

                        </div>
                    )
                }

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Pre Program Actions
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <button
                    type="button"

                    disabled={
                        isOnboardingStatusLoading
                        ||
                        isOnboardingStatusError
                    }

                    onClick={
                        () =>
                            setShowOnboardingDetails(
                                previous =>
                                    !previous
                            )
                    }

                    className="
                        flex
                        justify-start
                        items-center
                        gap-1
                    "
                >

                    <PreProgramSummaryChip
                        value={
                            onboardingSummary
                        }
                    />


                    {
                        showOnboardingDetails
                            ?
                            (
                                <ChevronUpIcon
                                    className="
                                        h-3
                                        w-3
                                        text-orange-600
                                    "
                                />
                            )
                            :
                            (
                                <ChevronDownIcon
                                    className="
                                        h-3
                                        w-3
                                        text-orange-600
                                    "
                                />
                            )
                    }

                </button>



                {
                    showOnboardingDetails
                    &&
                    !isOnboardingStatusLoading
                    &&
                    !isOnboardingStatusError
                    &&
                    (
                        <div
                            className="
                                mt-3
                                rounded
                                bg-gray-50
                                p-3
                                space-y-4
                            "
                        >

                            <PreProgramActionEditor

                                label="Contact Details"

                                status={
                                    onboardingStatus
                                        ?.actions
                                        ?.contactDetails
                                        ?.status
                                    ||
                                    'pending'
                                }

                                canEdit={
                                    canManageInitiation
                                }

                                isUpdating={
                                    isUpdatingOnboarding
                                    &&
                                    updatingActionName ===
                                        'contactDetails'
                                }

                                onChange={
                                    status =>
                                        handleUpdateJourneyAction({

                                            actionName:
                                                'contactDetails',

                                            status,

                                            currentStatus:
                                                onboardingStatus
                                                    ?.actions
                                                    ?.contactDetails
                                                    ?.status
                                                ||
                                                'pending',

                                        })
                                }

                            />


                            <PreProgramActionEditor

                                label="Digital Pic"

                                status={
                                    onboardingStatus
                                        ?.actions
                                        ?.digitalPic
                                        ?.status
                                    ||
                                    'pending'
                                }

                                canEdit={
                                    canManageInitiation
                                }

                                isUpdating={
                                    isUpdatingOnboarding
                                    &&
                                    updatingActionName ===
                                        'digitalPic'
                                }

                                onChange={
                                    status =>
                                        handleUpdateJourneyAction({

                                            actionName:
                                                'digitalPic',

                                            status,

                                            currentStatus:
                                                onboardingStatus
                                                    ?.actions
                                                    ?.digitalPic
                                                    ?.status
                                                ||
                                                'pending',

                                        })
                                }

                            />


                            <PreProgramActionEditor

                                label="AV Consent"

                                status={
                                    onboardingStatus
                                        ?.actions
                                        ?.avConsent
                                        ?.status
                                    ||
                                    'pending'
                                }

                                canEdit={
                                    canManageInitiation
                                }

                                isUpdating={
                                    isUpdatingOnboarding
                                    &&
                                    updatingActionName ===
                                        'avConsent'
                                }

                                onChange={
                                    status =>
                                        handleUpdateJourneyAction({

                                            actionName:
                                                'avConsent',

                                            status,

                                            currentStatus:
                                                onboardingStatus
                                                    ?.actions
                                                    ?.avConsent
                                                    ?.status
                                                ||
                                                'pending',

                                        })
                                }

                            />

                        </div>
                    )
                }


                {
                    onboardingUpdateError
                    &&
                    (
                        <p
                            className="
                                text-[8px]
                                text-red-600
                                font-bold
                                mt-2
                            "
                        >
                            {
                                onboardingUpdateError
                            }
                        </p>
                    )
                }


            </div>



            {/*
            |--------------------------------------------------------------------------
            | Initial 1:1
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <JourneyMilestoneEditor

                    booking={
                        slotStatus
                            ?.initial1on1
                    }

                    completion={
                        onboardingStatus
                            ?.milestones
                            ?.initial1on1
                    }

                    canEdit={
                        canManageInitiation
                    }

                    isUpdating={
                        isUpdatingOnboarding
                        &&
                        updatingActionName ===
                            'initial1on1'
                    }

                    isLoading={
                        isSlotStatusLoading
                        ||
                        isOnboardingStatusLoading
                    }

                    isError={
                        isSlotStatusError
                        ||
                        isOnboardingStatusError
                    }

                    onChange={
                        status =>
                            handleUpdateJourneyAction({

                                actionName:
                                    'initial1on1',

                                status,

                                currentStatus:
                                    onboardingStatus
                                        ?.milestones
                                        ?.initial1on1
                                        ?.status
                                    ||
                                    'pending',

                            })
                    }

                />

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Kickoff
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <EventAttendanceMilestoneEditor

                    value={
                        onboardingStatus
                            ?.milestones
                            ?.kickoff
                    }

                    canEdit={
                        canManageInitiation
                    }

                    isUpdating={
                        isUpdatingOnboarding
                        &&
                        updatingActionName ===
                            'kickoff'
                    }

                    isLoading={
                        isOnboardingStatusLoading
                    }

                    isError={
                        isOnboardingStatusError
                    }

                    onChange={
                        status =>
                            handleUpdateJourneyAction({

                                actionName:
                                    'kickoff',

                                status,

                                currentStatus:
                                    onboardingStatus
                                        ?.milestones
                                        ?.kickoff
                                        ?.status
                                    ||
                                    'pending',

                            })
                    }

                />

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Induction
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <EventAttendanceMilestoneEditor

                    value={
                        onboardingStatus
                            ?.milestones
                            ?.induction
                    }

                    canEdit={
                        canManageInitiation
                    }

                    isUpdating={
                        isUpdatingOnboarding
                        &&
                        updatingActionName ===
                            'induction'
                    }

                    isLoading={
                        isOnboardingStatusLoading
                    }

                    isError={
                        isOnboardingStatusError
                    }

                    onChange={
                        status =>
                            handleUpdateJourneyAction({

                                actionName:
                                    'induction',

                                status,

                                currentStatus:
                                    onboardingStatus
                                        ?.milestones
                                        ?.induction
                                        ?.status
                                    ||
                                    'pending',

                            })
                    }

                />

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Deep Dive
            |--------------------------------------------------------------------------
            */}

            <div className="px-4 py-4">

                <JourneyMilestoneEditor

                    booking={
                        slotStatus
                            ?.deepDive
                    }

                    completion={
                        onboardingStatus
                            ?.milestones
                            ?.deepDive
                    }

                    canEdit={
                        canManageInitiation
                    }

                    isUpdating={
                        isUpdatingOnboarding
                        &&
                        updatingActionName ===
                            'deepDive'
                    }

                    isLoading={
                        isSlotStatusLoading
                        ||
                        isOnboardingStatusLoading
                    }

                    isError={
                        isSlotStatusError
                        ||
                        isOnboardingStatusError
                    }

                    onChange={
                        status =>
                            handleUpdateJourneyAction({

                                actionName:
                                    'deepDive',

                                status,

                                currentStatus:
                                    onboardingStatus
                                        ?.milestones
                                        ?.deepDive
                                        ?.status
                                    ||
                                    'pending',

                            })
                    }

                />

            </div>


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Event Attendance Milestone Editor - 25 Sep 2026
|--------------------------------------------------------------------------
|
| Used by:
|
| Kickoff
| Induction
|
| Status:
|
| Pending
| Confirmed
| Attended
| Not Required
|
*/

const EventAttendanceMilestoneEditor = ({

    value,

    canEdit,

    isUpdating,

    isLoading,

    isError,

    onChange,

}) => {


    const [
        showDetails,
        setShowDetails,
    ] =
        useState(
            false
        )


    if (
        isLoading
    ) {

        return (

            <Pill
                value="Loading..."
                className="
                    bg-gray-100
                    text-gray-500
                "
            />

        )

    }


    if (
        isError
    ) {

        return (

            <Pill
                value="Unavailable"
                className="
                    bg-red-50
                    text-red-700
                "
            />

        )

    }


    const status =
        normalizeValue(
            value
                ?.status
            ||
            'pending'
        )


    return (

        <div>


            {/*
            |--------------------------------------------------------------------------
            | Summary
            |--------------------------------------------------------------------------
            */}

            <button
                type="button"

                onClick={
                    () =>
                        setShowDetails(
                            previous =>
                                !previous
                        )
                }

                className="
                    flex
                    justify-start
                    items-center
                    gap-1
                "
            >

                <EventAttendanceStatusChip
                    value={
                        status
                    }
                />


                {
                    showDetails
                        ?
                        (
                            <ChevronUpIcon
                                className="
                                    h-3
                                    w-3
                                    text-orange-600
                                "
                            />
                        )
                        :
                        (
                            <ChevronDownIcon
                                className="
                                    h-3
                                    w-3
                                    text-orange-600
                                "
                            />
                        )
                }

            </button>



            {/*
            |--------------------------------------------------------------------------
            | Key Date Under Status
            |--------------------------------------------------------------------------
            */}

            {
                status ===
                    'confirmed'
                &&
                value
                    ?.confirmedAt
                &&
                (
                    <p
                        className="
                            text-[8px]
                            text-gray-500
                            mt-2
                        "
                    >
                        Confirmed {
                            formatDateTime(
                                value
                                    ?.confirmedAt
                            )
                        }
                    </p>
                )
            }


            {
                status ===
                    'attended'
                &&
                value
                    ?.attendedAt
                &&
                (
                    <p
                        className="
                            text-[8px]
                            text-gray-500
                            mt-2
                        "
                    >
                        Attended {
                            formatDateTime(
                                value
                                    ?.attendedAt
                            )
                        }
                    </p>
                )
            }



            {/*
            |--------------------------------------------------------------------------
            | Expanded
            |--------------------------------------------------------------------------
            */}

            {
                showDetails
                &&
                (
                    <div
                        className="
                            mt-3
                            bg-gray-50
                            rounded
                            p-3
                        "
                    >


                        <div
                            className="
                                flex
                                justify-between
                                items-center
                                gap-2
                            "
                        >

                            <p
                                className="
                                    text-[9px]
                                    font-bold
                                    text-gray-500
                                "
                            >
                                Attendance
                            </p>


                            {
                                isUpdating
                                &&
                                (
                                    <p
                                        className="
                                            text-[8px]
                                            font-bold
                                            text-orange-600
                                        "
                                    >
                                        Saving...
                                    </p>
                                )
                            }

                        </div>



                        <div
                            className="
                                flex
                                flex-wrap
                                gap-1
                                mt-2
                            "
                        >

                            <JourneyActionButton

                                label="Pending"

                                active={
                                    status ===
                                    'pending'
                                }

                                disabled={
                                    !canEdit
                                    ||
                                    isUpdating
                                }

                                activeClassName="
                                    bg-gray-600
                                    text-white
                                "

                                inactiveClassName="
                                    bg-gray-100
                                    text-gray-600
                                    hover:bg-gray-200
                                "

                                onClick={
                                    () =>
                                        onChange(
                                            'pending'
                                        )
                                }

                            />


                            <JourneyActionButton

                                label="Confirmed"

                                active={
                                    status ===
                                    'confirmed'
                                }

                                disabled={
                                    !canEdit
                                    ||
                                    isUpdating
                                }

                                activeClassName="
                                    bg-blue-600
                                    text-white
                                "

                                inactiveClassName="
                                    bg-blue-100
                                    text-blue-700
                                    hover:bg-blue-200
                                "

                                onClick={
                                    () =>
                                        onChange(
                                            'confirmed'
                                        )
                                }

                            />


                            <JourneyActionButton

                                label="Attended"

                                active={
                                    status ===
                                    'attended'
                                }

                                disabled={
                                    !canEdit
                                    ||
                                    isUpdating
                                }

                                activeClassName="
                                    bg-lime-600
                                    text-white
                                "

                                inactiveClassName="
                                    bg-lime-100
                                    text-lime-700
                                    hover:bg-lime-200
                                "

                                onClick={
                                    () =>
                                        onChange(
                                            'attended'
                                        )
                                }

                            />


                            <JourneyActionButton

                                label="Not Required"

                                active={
                                    status ===
                                    'not_required'
                                }

                                disabled={
                                    !canEdit
                                    ||
                                    isUpdating
                                }

                                activeClassName="
                                    bg-purple-600
                                    text-white
                                "

                                inactiveClassName="
                                    bg-purple-50
                                    text-purple-700
                                    hover:bg-purple-100
                                "

                                onClick={
                                    () =>
                                        onChange(
                                            'not_required'
                                        )
                                }

                            />

                        </div>



                        {/*
                        |--------------------------------------------------------------------------
                        | Confirmation History
                        |--------------------------------------------------------------------------
                        */}

                        {
                            value
                                ?.confirmedAt
                            &&
                            (
                                <div
                                    className="
                                        mt-3
                                        pt-2
                                        border-t
                                        border-gray-200
                                    "
                                >

                                    <p
                                        className="
                                            text-[8px]
                                            text-gray-400
                                        "
                                    >
                                        Confirmed {
                                            formatDateTime(
                                                value
                                                    ?.confirmedAt
                                            )
                                        }
                                    </p>

                                </div>
                            )
                        }



                        {/*
                        |--------------------------------------------------------------------------
                        | Attendance History
                        |--------------------------------------------------------------------------
                        */}

                        {
                            value
                                ?.attendedAt
                            &&
                            (
                                <p
                                    className="
                                        text-[8px]
                                        text-gray-400
                                        mt-1
                                    "
                                >
                                    Attended {
                                        formatDateTime(
                                            value
                                                ?.attendedAt
                                        )
                                    }
                                </p>
                            )
                        }



                        {
                            !canEdit
                            &&
                            (
                                <p
                                    className="
                                        text-[8px]
                                        text-gray-400
                                        italic
                                        mt-2
                                    "
                                >
                                    View only
                                </p>
                            )
                        }


                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Event Attendance Status Chip
|--------------------------------------------------------------------------
*/

const EventAttendanceStatusChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    if (
        normalized ===
        'attended'
    ) {

        return (

            <Pill
                value="Attended"
                className="
                    bg-lime-100
                    text-lime-700
                "
            />

        )

    }


    if (
        normalized ===
        'confirmed'
    ) {

        return (

            <Pill
                value="Confirmed"
                className="
                    bg-blue-100
                    text-blue-700
                "
            />

        )

    }


    if (
        normalized ===
        'not_required'
    ) {

        return (

            <Pill
                value="Not Required"
                className="
                    bg-purple-100
                    text-purple-700
                "
            />

        )

    }


    return (

        <Pill
            value="Pending"
            className="
                bg-gray-100
                text-gray-600
            "
        />

    )

}



/*
|--------------------------------------------------------------------------
| SlotBoard Journey Milestone Editor
|--------------------------------------------------------------------------
|
| Used by:
|
| Initial 1:1
| Deep Dive
|
| Combines:
|
| SlotBoard booking state
| Onboarding completion state
|
*/

const JourneyMilestoneEditor = ({

    booking,

    completion,

    canEdit,

    isUpdating,

    isLoading,

    isError,

    onChange,

}) => {


    const [
        showDetails,
        setShowDetails,
    ] =
        useState(
            false
        )


    if (
        isLoading
    ) {

        return (

            <Pill
                value="Loading..."
                className="
                    bg-gray-100
                    text-gray-500
                "
            />

        )

    }


    if (
        isError
    ) {

        return (

            <Pill
                value="Unavailable"
                className="
                    bg-red-50
                    text-red-700
                "
            />

        )

    }


    const bookingStatus =
        normalizeValue(
            booking
                ?.status
        )


    const completionStatus =
        normalizeValue(
            completion
                ?.status
            ||
            'pending'
        )


    /*
    |--------------------------------------------------------------------------
    | Primary Status
    |--------------------------------------------------------------------------
    */

    let primaryStatus =
        'not_booked'


    if (
        completionStatus ===
        'completed'
    ) {

        primaryStatus =
            'completed'

    } else if (
        completionStatus ===
        'not_required'
    ) {

        primaryStatus =
            'not_required'

    } else if (
        bookingStatus ===
        'booked'
    ) {

        primaryStatus =
            'booked'

    }


    return (

        <div>


            <button
                type="button"

                onClick={
                    () =>
                        setShowDetails(
                            previous =>
                                !previous
                        )
                }

                className="
                    flex
                    justify-start
                    items-center
                    gap-1
                "
            >

                <JourneyMilestoneChip
                    value={
                        primaryStatus
                    }
                />


                {
                    showDetails
                        ?
                        (
                            <ChevronUpIcon
                                className="
                                    h-3
                                    w-3
                                    text-orange-600
                                "
                            />
                        )
                        :
                        (
                            <ChevronDownIcon
                                className="
                                    h-3
                                    w-3
                                    text-orange-600
                                "
                            />
                        )
                }

            </button>



            {
                bookingStatus ===
                    'booked'
                &&
                booking
                    ?.slotStart
                &&
                (
                    <div className="mt-2">

                        <p
                            className="
                                text-[9px]
                                font-bold
                                text-gray-700
                            "
                        >
                            {
                                formatSlotDate(
                                    booking
                                        ?.slotStart,

                                    booking
                                        ?.slotTimezone
                                )
                            }
                        </p>


                        <p
                            className="
                                text-[8px]
                                text-gray-500
                                mt-0.5
                            "
                        >
                            {
                                formatSlotTimeRange(
                                    booking
                                        ?.slotStart,

                                    booking
                                        ?.slotEnd,

                                    booking
                                        ?.slotTimezone
                                )
                            }
                        </p>

                    </div>
                )
            }



            {
                showDetails
                &&
                (
                    <div
                        className="
                            mt-3
                            bg-gray-50
                            rounded
                            p-3
                        "
                    >


                        <div
                            className="
                                flex
                                justify-between
                                items-center
                                gap-2
                            "
                        >

                            <p
                                className="
                                    text-[9px]
                                    font-bold
                                    text-gray-500
                                "
                            >
                                Booking
                            </p>


                            <BookingStatusChip
                                value={
                                    bookingStatus
                                }
                            />

                        </div>



                        {
                            bookingStatus ===
                                'booked'
                            &&
                            (
                                <div
                                    className="
                                        mt-2
                                        text-[8px]
                                        text-gray-500
                                        leading-4
                                    "
                                >

                                    <p>
                                        {
                                            formatSlotDate(
                                                booking
                                                    ?.slotStart,

                                                booking
                                                    ?.slotTimezone
                                            )
                                        }
                                    </p>

                                    <p>
                                        {
                                            formatSlotTimeRange(
                                                booking
                                                    ?.slotStart,

                                                booking
                                                    ?.slotEnd,

                                                booking
                                                    ?.slotTimezone
                                            )
                                        }
                                    </p>

                                    {
                                        booking
                                            ?.slotTimezone
                                        &&
                                        (
                                            <p>
                                                {
                                                    formatTimezoneLabel(
                                                        booking
                                                            ?.slotTimezone
                                                    )
                                                }
                                            </p>
                                        )
                                    }

                                </div>
                            )
                        }



                        <div
                            className="
                                border-t
                                border-gray-200
                                mt-3
                                pt-3
                            "
                        >

                            <div
                                className="
                                    flex
                                    justify-between
                                    items-center
                                "
                            >

                                <p
                                    className="
                                        text-[9px]
                                        font-bold
                                        text-gray-500
                                    "
                                >
                                    Completion
                                </p>


                                {
                                    isUpdating
                                    &&
                                    (
                                        <p
                                            className="
                                                text-[8px]
                                                text-orange-600
                                                font-bold
                                            "
                                        >
                                            Saving...
                                        </p>
                                    )
                                }

                            </div>



                            <div
                                className="
                                    flex
                                    flex-wrap
                                    gap-1
                                    mt-2
                                "
                            >

                                <JourneyActionButton

                                    label="Pending"

                                    active={
                                        completionStatus ===
                                        'pending'
                                    }

                                    disabled={
                                        !canEdit
                                        ||
                                        isUpdating
                                    }

                                    activeClassName="
                                        bg-gray-600
                                        text-white
                                    "

                                    inactiveClassName="
                                        bg-gray-100
                                        text-gray-600
                                        hover:bg-gray-200
                                    "

                                    onClick={
                                        () =>
                                            onChange(
                                                'pending'
                                            )
                                    }

                                />


                                <JourneyActionButton

                                    label="Completed"

                                    active={
                                        completionStatus ===
                                        'completed'
                                    }

                                    disabled={
                                        !canEdit
                                        ||
                                        isUpdating
                                    }

                                    activeClassName="
                                        bg-lime-600
                                        text-white
                                    "

                                    inactiveClassName="
                                        bg-lime-100
                                        text-lime-700
                                        hover:bg-lime-200
                                    "

                                    onClick={
                                        () =>
                                            onChange(
                                                'completed'
                                            )
                                    }

                                />


                                <JourneyActionButton

                                    label="Not Required"

                                    active={
                                        completionStatus ===
                                        'not_required'
                                    }

                                    disabled={
                                        !canEdit
                                        ||
                                        isUpdating
                                    }

                                    activeClassName="
                                        bg-blue-600
                                        text-white
                                    "

                                    inactiveClassName="
                                        bg-blue-50
                                        text-blue-700
                                        hover:bg-blue-100
                                    "

                                    onClick={
                                        () =>
                                            onChange(
                                                'not_required'
                                            )
                                    }

                                />

                            </div>



                            {
                                completionStatus ===
                                    'completed'
                                &&
                                completion
                                    ?.completedAt
                                &&
                                (
                                    <p
                                        className="
                                            text-[8px]
                                            text-gray-400
                                            mt-2
                                        "
                                    >
                                        Completed {
                                            formatDateTime(
                                                completion
                                                    ?.completedAt
                                            )
                                        }
                                    </p>
                                )
                            }


                            {
                                !canEdit
                                &&
                                (
                                    <p
                                        className="
                                            text-[8px]
                                            text-gray-400
                                            italic
                                            mt-2
                                        "
                                    >
                                        View only
                                    </p>
                                )
                            }

                        </div>


                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Journey Milestone Chip
|--------------------------------------------------------------------------
*/

const JourneyMilestoneChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    if (
        normalized ===
        'completed'
    ) {

        return (

            <Pill
                value="Completed"
                className="
                    bg-lime-100
                    text-lime-700
                "
            />

        )

    }


    if (
        normalized ===
        'booked'
    ) {

        return (

            <Pill
                value="Booked"
                className="
                    bg-blue-100
                    text-blue-700
                "
            />

        )

    }


    if (
        normalized ===
        'not_required'
    ) {

        return (

            <Pill
                value="Not Required"
                className="
                    bg-blue-50
                    text-blue-700
                "
            />

        )

    }


    return (

        <Pill
            value="Not Booked"
            className="
                bg-gray-100
                text-gray-600
            "
        />

    )

}



/*
|--------------------------------------------------------------------------
| Booking Status Chip
|--------------------------------------------------------------------------
*/

const BookingStatusChip = ({
    value,
}) => {


    if (
        normalizeValue(
            value
        ) ===
        'booked'
    ) {

        return (

            <Pill
                value="Booked"
                className="
                    bg-blue-100
                    text-blue-700
                "
            />

        )

    }


    return (

        <Pill
            value="Not Booked"
            className="
                bg-gray-100
                text-gray-600
            "
        />

    )

}



/*
|--------------------------------------------------------------------------
| Pre Program Action Editor
|--------------------------------------------------------------------------
*/

const PreProgramActionEditor = ({

    label,

    status,

    canEdit,

    isUpdating,

    onChange,

}) => {


    const normalizedStatus =
        normalizeValue(
            status
        )


    return (

        <div>


            <div
                className="
                    flex
                    justify-between
                    items-center
                    gap-2
                "
            >

                <p
                    className="
                        text-[10px]
                        font-bold
                        text-gray-700
                    "
                >
                    {
                        label
                    }
                </p>


                {
                    isUpdating
                    &&
                    (
                        <p
                            className="
                                text-[8px]
                                font-bold
                                text-orange-600
                            "
                        >
                            Saving...
                        </p>
                    )
                }

            </div>



            <div
                className="
                    flex
                    flex-wrap
                    gap-1
                    mt-2
                "
            >

                <JourneyActionButton

                    label="Pending"

                    active={
                        normalizedStatus ===
                        'pending'
                    }

                    disabled={
                        !canEdit
                        ||
                        isUpdating
                    }

                    activeClassName="
                        bg-gray-600
                        text-white
                    "

                    inactiveClassName="
                        bg-gray-100
                        text-gray-600
                        hover:bg-gray-200
                    "

                    onClick={
                        () =>
                            onChange(
                                'pending'
                            )
                    }

                />


                <JourneyActionButton

                    label="Completed"

                    active={
                        normalizedStatus ===
                        'completed'
                    }

                    disabled={
                        !canEdit
                        ||
                        isUpdating
                    }

                    activeClassName="
                        bg-lime-600
                        text-white
                    "

                    inactiveClassName="
                        bg-lime-100
                        text-lime-700
                        hover:bg-lime-200
                    "

                    onClick={
                        () =>
                            onChange(
                                'completed'
                            )
                    }

                />


                <JourneyActionButton

                    label="Not Required"

                    active={
                        normalizedStatus ===
                        'not_required'
                    }

                    disabled={
                        !canEdit
                        ||
                        isUpdating
                    }

                    activeClassName="
                        bg-blue-600
                        text-white
                    "

                    inactiveClassName="
                        bg-blue-50
                        text-blue-700
                        hover:bg-blue-100
                    "

                    onClick={
                        () =>
                            onChange(
                                'not_required'
                            )
                    }

                />

            </div>


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Journey Action Button
|--------------------------------------------------------------------------
*/

const JourneyActionButton = ({

    label,

    active,

    disabled,

    activeClassName,

    inactiveClassName,

    onClick,

}) => {

    return (

        <button
            type="button"

            disabled={
                disabled
            }

            onClick={
                onClick
            }

            className={`
                rounded-full
                px-2
                py-1
                text-[8px]
                font-bold
                whitespace-nowrap
                transition

                ${
                    disabled
                        ?
                        'cursor-default opacity-70'
                        :
                        'cursor-pointer'
                }

                ${
                    active
                        ?
                        activeClassName
                        :
                        inactiveClassName
                }
            `}
        >
            {
                label
            }
        </button>

    )

}



/*
|--------------------------------------------------------------------------
| Journey Scroll Button
|--------------------------------------------------------------------------
*/

const JourneyScrollButton = ({

    direction,

    enabled,

    onClick,

}) => {


    const Icon =
        direction ===
        'left'
            ?
            ChevronLeftIcon
            :
            ChevronRightIcon


    return (

        <button
            type="button"

            disabled={
                !enabled
            }

            onClick={
                onClick
            }

            className={`
                h-8
                w-8
                rounded-full
                flex
                justify-center
                items-center
                border
                transition

                ${
                    enabled
                        ?
                        `
                            border-orange-200
                            bg-orange-50
                            text-orange-600
                            hover:bg-orange-100
                            cursor-pointer
                        `
                        :
                        `
                            border-gray-200
                            bg-gray-50
                            text-gray-300
                            cursor-default
                        `
                }
            `}
        >

            <Icon
                className="
                    h-4
                    w-4
                "
            />

        </button>

    )

}



/*
|--------------------------------------------------------------------------
| Journey Data Error
|--------------------------------------------------------------------------
*/

const JourneyDataError = ({

    title,

    error,

}) => {

    return (

        <div
            className="
                bg-orange-50
                rounded
                m-1
                p-3
            "
        >

            <p
                className="
                    text-xs
                    font-bold
                    text-orange-700
                "
            >
                {
                    title
                }
            </p>

            <p
                className="
                    text-[10px]
                    text-orange-700
                    mt-1
                "
            >
                {
                    error
                        ?.data
                        ?.message
                    ||
                    error
                        ?.error
                    ||
                    'Unable to load journey status.'
                }
            </p>

        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Table Heading
|--------------------------------------------------------------------------
*/

const TableHeading = ({
    children,
}) => {

    return (

        <p
            className="
                text-[9px]
                uppercase
                font-bold
                text-gray-500
                whitespace-nowrap
            "
        >
            {
                children
            }
        </p>

    )

}



/*
|--------------------------------------------------------------------------
| Summary Card
|--------------------------------------------------------------------------
*/

const SummaryCard = ({

    label,

    value,

    className = '',

    labelClassName = '',

}) => {

    return (

        <div
            className={`
                rounded
                p-4
                ${className}
            `}
        >

            <p
                className={`
                    text-[9px]
                    uppercase
                    font-bold
                    ${labelClassName}
                `}
            >
                {
                    label
                }
            </p>

            <p
                className="
                    text-2xl
                    font-bold
                    text-gray-800
                    mt-2
                "
            >
                {
                    value
                }
            </p>

        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Pre Program Summary
|--------------------------------------------------------------------------
*/

const getPreProgramActionSummary = ({

    onboardingStatus,

    isLoading,

    isError,

}) => {


    if (
        isLoading
    ) {

        return 'loading'

    }


    if (
        isError
    ) {

        return 'unavailable'

    }


    return (
        onboardingStatus
            ?.summaryStatus
        ||
        'not_started'
    )

}



/*
|--------------------------------------------------------------------------
| Pre Program Summary Chip
|--------------------------------------------------------------------------
*/

const PreProgramSummaryChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    if (
        normalized ===
        'complete'
    ) {

        return (

            <Pill
                value="Complete"
                className="
                    bg-lime-100
                    text-lime-700
                "
            />

        )

    }


    if (
        normalized ===
        'partial'
    ) {

        return (

            <Pill
                value="Partial"
                className="
                    bg-orange-100
                    text-orange-700
                "
            />

        )

    }


    if (
        normalized ===
        'loading'
    ) {

        return (

            <Pill
                value="Loading..."
                className="
                    bg-gray-100
                    text-gray-500
                "
            />

        )

    }


    if (
        normalized ===
        'unavailable'
    ) {

        return (

            <Pill
                value="Unavailable"
                className="
                    bg-red-50
                    text-red-700
                "
            />

        )

    }


    return (

        <Pill
            value="Not Started"
            className="
                bg-gray-100
                text-gray-600
            "
        />

    )

}



/*
|--------------------------------------------------------------------------
| Communication Summary
|--------------------------------------------------------------------------
*/

const getCommunicationSummary = ({

    communicationStatus,

    isLoading,

    isError,

}) => {


    if (
        isLoading
    ) {

        return 'loading'

    }


    if (
        isError
    ) {

        return 'unavailable'

    }


    const milestones = [

        communicationStatus
            ?.welcome,

        communicationStatus
            ?.onboarding_update,

        communicationStatus
            ?.initial_1on1,

        communicationStatus
            ?.deep_dive,

    ]


    const sentCount =
        milestones
            .filter(
                milestone =>
                    normalizeValue(
                        milestone
                            ?.status
                    ) ===
                    'sent'
            )
            .length


    const hasAnyCommunication =
        milestones
            .some(
                milestone => {

                    const status =
                        normalizeValue(
                            milestone
                                ?.status
                        )


                    return (
                        status
                        &&
                        status !==
                        'not_sent'
                        &&
                        status !==
                        'not sent'
                    )

                }
            )


    if (
        sentCount ===
        milestones.length
    ) {

        return 'all_sent'

    }


    if (
        sentCount ===
            0
        &&
        !hasAnyCommunication
    ) {

        return 'not_sent'

    }


    return 'partial'

}



/*
|--------------------------------------------------------------------------
| Communication Summary Chip
|--------------------------------------------------------------------------
*/

const CommunicationSummaryChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    if (
        normalized ===
        'all_sent'
    ) {

        return (
            <Pill
                value="All Sent"
                className="bg-lime-100 text-lime-700"
            />
        )

    }


    if (
        normalized ===
        'partial'
    ) {

        return (
            <Pill
                value="Partial"
                className="bg-orange-100 text-orange-700"
            />
        )

    }


    if (
        normalized ===
        'loading'
    ) {

        return (
            <Pill
                value="Loading..."
                className="bg-gray-100 text-gray-500"
            />
        )

    }


    if (
        normalized ===
        'unavailable'
    ) {

        return (
            <Pill
                value="Unavailable"
                className="bg-red-50 text-red-700"
            />
        )

    }


    return (
        <Pill
            value="Not Sent"
            className="bg-gray-100 text-gray-500"
        />
    )

}



/*
|--------------------------------------------------------------------------
| Communication Milestone
|--------------------------------------------------------------------------
*/

const CommunicationMilestone = ({

    label,

    milestone,

}) => {

    return (

        <div
            className="
                flex
                justify-between
                items-center
                gap-2
            "
        >

            <p
                className="
                    text-[9px]
                    font-bold
                    text-gray-600
                "
            >
                {
                    label
                }
            </p>


            <CommunicationStatusChip
                value={
                    milestone
                        ?.status
                    ||
                    'not_sent'
                }
            />

        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Communication Status
|--------------------------------------------------------------------------
*/

const CommunicationStatusChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    if (
        normalized ===
        'sent'
    ) {

        return (
            <Pill
                value="Sent"
                className="bg-lime-100 text-lime-700"
            />
        )

    }


    if (
        normalized ===
        'draft'
    ) {

        return (
            <Pill
                value="Draft"
                className="bg-yellow-100 text-yellow-700"
            />
        )

    }


    if (
        normalized ===
        'failed'
    ) {

        return (
            <Pill
                value="Failed"
                className="bg-red-100 text-red-700"
            />
        )

    }


    return (
        <Pill
            value="Not Sent"
            className="bg-gray-100 text-gray-500"
        />
    )

}



/*
|--------------------------------------------------------------------------
| Initiation Status
|--------------------------------------------------------------------------
*/

const getInitiationStatus = ({

    decisionStatus,

    finalDecision,

}) => {


    if (
        normalizeValue(
            finalDecision
        ) !==
        'go'
    ) {

        return null

    }


    if (
        normalizeValue(
            decisionStatus
        ) ===
        'finalized'
    ) {

        return 'Intention Finalized'

    }


    return 'Initiation Started'

}



/*
|--------------------------------------------------------------------------
| Status Chips
|--------------------------------------------------------------------------
*/

const CandidateStatusChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    let className =
        'bg-gray-100 text-gray-600'


    if (
        normalized ===
        'submitted'
    ) {

        className =
            'bg-blue-50 text-blue-700'

    }


    if (
        normalized ===
        'draft'
    ) {

        className =
            'bg-orange-50 text-orange-700'

    }


    if (
        normalized ===
        'accessed'
    ) {

        className =
            'bg-purple-50 text-purple-700'

    }


    return (
        <Pill
            value={
                formatStatus(
                    value
                )
            }
            className={
                className
            }
        />
    )

}



const DecisionChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    let className =
        'bg-gray-100 text-gray-600'


    if (
        normalized ===
        'aligned'
    ) {

        className =
            'bg-lime-50 text-lime-700'

    }


    if (
        normalized ===
        'finalized'
    ) {

        className =
            'bg-purple-50 text-purple-700'

    }


    if (
        normalized ===
        'pending'
    ) {

        className =
            'bg-yellow-50 text-yellow-700'

    }


    if (
        normalized ===
        'requires_alignment'
    ) {

        className =
            'bg-orange-50 text-orange-700'

    }


    return (
        <Pill
            value={
                formatStatus(
                    value
                )
            }
            className={
                className
            }
        />
    )

}



const FinalDecisionChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    let className =
        'bg-gray-100 text-gray-600'


    if (
        normalized ===
        'go'
    ) {

        className =
            'bg-lime-100 text-lime-700'

    }


    if (
        normalized ===
        'next_cohort'
    ) {

        className =
            'bg-blue-50 text-blue-700'

    }


    if (
        normalized ===
        'no_go'
    ) {

        className =
            'bg-red-50 text-red-700'

    }


    return (
        <Pill
            value={
                formatStatus(
                    value
                )
            }
            className={
                className
            }
        />
    )

}



const InitiationChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    return (
        <Pill
            value={
                value
            }
            className={
                normalized ===
                    'intention finalized'
                    ?
                    'bg-purple-100 text-purple-700'
                    :
                    'bg-orange-100 text-orange-700'
            }
        />
    )

}



const BoardStatusChip = ({
    value,
}) => {


    const normalized =
        normalizeValue(
            value
        )


    let className =
        'bg-orange-100 text-orange-700'


    if (
        normalized ===
        'open'
    ) {

        className =
            'bg-lime-100 text-lime-700'

    }


    if (
        normalized ===
        'closed'
    ) {

        className =
            'bg-gray-200 text-gray-700'

    }


    if (
        normalized ===
        'archived'
    ) {

        className =
            'bg-purple-100 text-purple-700'

    }


    return (
        <Pill
            value={
                formatStatus(
                    value
                    ||
                    'draft'
                )
            }
            className={
                className
            }
        />
    )

}



const ActiveChip = ({
    isActive,
}) => {

    return (
        <Pill
            value={
                isActive
                    ?
                    'Active'
                    :
                    'Inactive'
            }
            className={
                isActive
                    ?
                    'bg-lime-100 text-lime-700'
                    :
                    'bg-gray-200 text-gray-600'
            }
        />
    )

}



/*
|--------------------------------------------------------------------------
| Generic Pill
|--------------------------------------------------------------------------
*/

const Pill = ({

    value,

    className,

}) => {

    return (

        <span
            className={`
                inline-flex
                justify-center
                items-center
                rounded-full
                px-2.5
                py-1
                text-[9px]
                font-bold
                whitespace-nowrap
                ${className}
            `}
        >
            {
                value
            }
        </span>

    )

}



/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

const normalizeValue =
    value => {

        return (
            value
                ?.toString()
                ?.trim()
                ?.toLowerCase()
            ||
            ''
        )

    }



const formatStatus =
    value => {


        if (
            !value
        ) {

            return ''

        }


        return value
            .toString()
            .replace(
                /_/g,
                ' '
            )
            .replace(
                /\b\w/g,
                character =>
                    character
                        .toUpperCase()
            )

    }



const formatDate =
    value => {


        if (
            !value
        ) {

            return 'Not Set'

        }


        const date =
            new Date(
                value
            )


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {

            return 'Not Set'

        }


        return new Intl.DateTimeFormat(
            'en-US',
            {
                year:
                    'numeric',

                month:
                    'short',

                day:
                    'numeric',
            }
        )
            .format(
                date
            )

    }



const formatDateTime =
    value => {


        if (
            !value
        ) {

            return ''

        }


        const date =
            new Date(
                value
            )


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {

            return ''

        }


        return new Intl.DateTimeFormat(
            'en-US',
            {

                month:
                    'short',

                day:
                    'numeric',

                year:
                    'numeric',

                hour:
                    'numeric',

                minute:
                    '2-digit',

            }
        )
            .format(
                date
            )

    }



const formatSlotDate =
    (
        value,
        timezone
    ) => {


        if (
            !value
        ) {

            return ''

        }


        const date =
            new Date(
                value
            )


        try {

            return new Intl.DateTimeFormat(
                'en-US',
                {

                    month:
                        'short',

                    day:
                        'numeric',

                    year:
                        'numeric',

                    ...(
                        timezone
                            ?
                            {
                                timeZone:
                                    timezone,
                            }
                            :
                            {}
                    ),

                }
            )
                .format(
                    date
                )

        } catch {

            return formatDate(
                value
            )

        }

    }



const formatSlotTimeRange =
    (
        slotStart,
        slotEnd,
        timezone
    ) => {


        if (
            !slotStart
        ) {

            return ''

        }


        const start =
            new Date(
                slotStart
            )


        const end =
            slotEnd
                ?
                new Date(
                    slotEnd
                )
                :
                null


        const options = {

            hour:
                'numeric',

            minute:
                '2-digit',

            ...(
                timezone
                    ?
                    {
                        timeZone:
                            timezone,
                    }
                    :
                    {}
            ),

        }


        try {

            const startTime =
                new Intl.DateTimeFormat(
                    'en-US',
                    options
                )
                    .format(
                        start
                    )


            if (
                !end
            ) {

                return startTime

            }


            const endTime =
                new Intl.DateTimeFormat(
                    'en-US',
                    options
                )
                    .format(
                        end
                    )


            return `${startTime} - ${endTime}`

        } catch {

            return ''

        }

    }



const formatTimezoneLabel =
    timezone => {


        const timezoneMap = {

            'America/Los_Angeles':
                'US Pacific',

            'America/Denver':
                'US Mountain',

            'America/Chicago':
                'US Central',

            'America/New_York':
                'US Eastern',

            'Europe/London':
                'UK',

            'Asia/Kolkata':
                'India',

        }


        return (
            timezoneMap[
                timezone
            ]
            ||
            timezone
            ||
            ''
        )

    }



export default EtOrgInitiationBoard